import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI, updateResumeState } from '@/api';
import { User, UserProfile } from '@/types';
import { supabase, ensureSessionExpiry, isSessionValid, resetSupabaseConnection, testDirectConnectivity, quickConnectivityCheck, checkCloudflareConnectivity } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { useResumeManager } from '@/hooks/useResumeManager';
import { compareVersions, fetchActiveVersionRule, getDeviceAppVersion } from '@/services/appVersionGate';
import { clearAllDrafts } from '@/lib/formDraftStorage';

import { logActivity, ActivityActions, logSessionActivity } from '@/utils/activityLogger';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<boolean>;
  isAuthenticated: boolean;
  isResuming: boolean;
  networkReady: boolean;
  profile: UserProfile | null;
  profileLoading: boolean;
  updateUser: (updates: Partial<UserProfile>) => Promise<void>;
  supabaseReady: boolean;
  updateRequired: boolean;
  updateLink: string | null;
  requiredAppVersion: string | null;
  deviceAppVersion: string | null;
  checkVersionGate: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Helper to set a 24-hour session expiry
const setSessionExpiry = () => {
  const expiresAt = Math.floor(Date.now() / 1000) + 86400; // 24 hours in seconds
  localStorage.setItem('santiyex-session-expiry', expiresAt.toString());
  return expiresAt;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const authDebug = (...args: unknown[]) => {
    console.log('[AuthDebug]', ...args);
  };

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [supabaseReady, setSupabaseReady] = useState(false);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [updateLink, setUpdateLink] = useState<string | null>(null);
  const [requiredAppVersion, setRequiredAppVersion] = useState<string | null>(null);
  const [deviceAppVersion, setDeviceAppVersion] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const clearForceUpdateState = useCallback(() => {
    setUpdateRequired(false);
    setUpdateLink(null);
    setRequiredAppVersion(null);
    setDeviceAppVersion(null);
    localStorage.removeItem('santiyex-force-update-required');
  }, []);

  const checkVersionGate = useCallback(async (): Promise<boolean> => {
    try {
      const appVersion = await getDeviceAppVersion();
      const activeRule = await fetchActiveVersionRule(supabase);

      if (!activeRule) {
        clearForceUpdateState();
        return true;
      }

      const isOutdated = compareVersions(appVersion, activeRule.minVersion) < 0;
      if (!isOutdated) {
        clearForceUpdateState();
        return true;
      }

      setUpdateRequired(true);
      setUpdateLink(activeRule.link);
      setRequiredAppVersion(activeRule.minVersion);
      setDeviceAppVersion(appVersion);
      localStorage.setItem(
        'santiyex-force-update-required',
        JSON.stringify({
          updateRequired: true,
          updateLink: activeRule.link,
          requiredAppVersion: activeRule.minVersion,
          deviceAppVersion: appVersion,
        })
      );
      return false;
    } catch (error) {
      authDebug('checkVersionGate:error', error);
      return true;
    }
  }, [clearForceUpdateState]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('santiyex-force-update-required');
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      if (parsed?.updateRequired) {
        setUpdateRequired(true);
        setUpdateLink(parsed.updateLink ?? null);
        setRequiredAppVersion(parsed.requiredAppVersion ?? null);
        setDeviceAppVersion(parsed.deviceAppVersion ?? null);
      }
    } catch (error) {
      authDebug('force-update-state:restore:error', error);
    }
  }, []);


  // Session tracking
  const sessionStartTime = React.useRef<Date | null>(null);
  const visitedPages = React.useRef<Set<string>>(new Set());
  const actionsCount = React.useRef<number>(0);

  // Global failsafe: never let auth loading spin forever.
  useEffect(() => {
    if (!loading) {
      return;
    }
    authDebug('loading-failsafe:armed');

    const loadingTimeout = setTimeout(() => {
      authDebug('loading-failsafe:triggered -> forcing loading/profileLoading false');
      setLoading(false);
      setProfileLoading(false);
    }, 6000);

    return () => clearTimeout(loadingTimeout);
  }, [loading]);

  // Track page visits for session analytics
  useEffect(() => {
    if (user && location.pathname) {
      visitedPages.current.add(location.pathname);
      actionsCount.current += 1;
    }
  }, [location.pathname, user]);

  // Use the resume manager hook to handle app resume and network readiness.
  // The app no longer BLOCKS on this (the reconnecting screen was removed), so
  // these timings only control how long API calls are briefly deferred while
  // the iOS network stack wakes up. Keep them short so resume feels instant.
  const { resumeReady, isResuming, appState } = useResumeManager({
    initialWaitTimeMs: 300,  // Brief settle time for the iOS network stack
    connectivityTimeoutMs: 2000,  // 2 second timeout for the connectivity check
    maxRetries: 1,  // Single quick check; queries retry on their own afterwards
    retryDelayMs: 500,  // Short delay if a retry is ever needed
  });

  const [networkConnected, setNetworkConnected] = useState(true);
  const [isNetworkReady, setIsNetworkReady] = useState(false);
  const previousAppState = React.useRef(appState);

  // Track if we're currently pinging Supabase
  const isPingingSupabaseRef = React.useRef(false);
  // Ensure FaceID auto-login is attempted at most once per app session
  const faceIDAttemptedRef = React.useRef(false);
  // Stable holder for the FaceID login routine so the auth listener can call the
  // latest version without re-subscribing.
  const attemptFaceIDLoginRef = React.useRef<() => Promise<void>>();

  // Update dependencies in useEffect
  useEffect(() => {
    previousAppState.current = appState;
  }, [appState]);

  // Update isNetworkReady when resumeReady changes
  useEffect(() => {
    setIsNetworkReady(resumeReady);
  }, [resumeReady]);

  // Update the global resume state tracker whenever resumeReady or isResuming changes
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      updateResumeState(isResuming, resumeReady);
    }
  }, [isResuming, resumeReady]);

  // --- Add fetchProfile ---
  const fetchProfile = async (userId: string) => {
      if (!userId) {
          setProfile(null);
          setProfileLoading(false);
          return;
      }

      setProfileLoading(true);
      try {
          const { data, error, status } = await supabase
              .from('users')
              .select('*')
              .eq('id', userId)
              .single();

          if (error && status !== 406) { // 406: No rows found (Not necessarily an error here)
              // Let consuming component handle null profile state
          } else if (data) {
              setProfile(data as UserProfile); // Assume data matches UserProfile structure
          } else {
               // Create a new profile if none exists
               await createProfile(userId);
          }
      } catch (err) {
          authDebug('fetchProfile:error', err);
      } finally {
          setProfileLoading(false);
      }
  };
  // --- End fetchProfile ---

  // --- Add createProfile ---
  const createProfile = async (userId: string) => {
      if (!userId) {
          return;
      }

      try {
          // Get basic user information from auth session
          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData?.session?.user) {
              return;
          }

          const sessionUser = sessionData.session.user;

          // Create initial profile with basic information
          const initialProfile = {
              id: userId,
              email: sessionUser.email || '',
              name: sessionUser.email?.split('@')[0] || 'User',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
          };

          const { data, error } = await supabase
              .from('users')
              .insert(initialProfile)
              .select();

          if (error) {
              authDebug('createProfile:error', error);
          } else if (data && data.length > 0) {
              setProfile(data[0] as UserProfile);
          }
      } catch (err) {
          authDebug('createProfile:error', err);
      }
  };
  // --- End createProfile ---

  // Lightweight Supabase health check for pre-warming connection
  const prewarmSupabaseConnection = async (): Promise<boolean> => {
    try {
      // Run health check in background without blocking UI
      const healthCheckPromise = supabase
        .from('users')
        .select('id')
        .limit(1);

      // Set a timeout to avoid hanging
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Health check timeout')), 5000)
      );

      const { error } = await Promise.race([
        healthCheckPromise,
        timeoutPromise
      ]);

      if (error) {
        setSupabaseReady(false);
        return false;
      }

      setSupabaseReady(true);
      return true;
    } catch (error) {
      setSupabaseReady(false);
      return false;
    }
  };

  // Refresh the user record from the API/cache. Kept in the public API for
  // call sites like LoginPage. It hydrates state but never blocks the UI and
  // never clears an existing user on transient failures (smooth auth).
  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authAPI.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        try {
          localStorage.setItem('santiyex-user-cache', JSON.stringify(currentUser));
        } catch (e) {
          // ignore localStorage errors
        }
        // Re-fetch profile in the background (don't block the caller)
        fetchProfile(currentUser.id);
        return true;
      }
      return false;
    } catch (error) {
      authDebug('refreshUser:error', error);
      // Keep any existing user/profile on failure to avoid bouncing to login.
      return false;
    }
  }, []);

  // Function to ping Supabase and reconnect if needed
  const pingSupabaseAndReconnect = async (): Promise<boolean> => {
    // Prevent concurrent pings
    if (isPingingSupabaseRef.current) {
      return false;
    }

    isPingingSupabaseRef.current = true;

    try {
      // Try a simple query to test connectivity
      const { error } = await supabase.from('projects').select('id').limit(1);

      if (error) {
        // If ping fails, reinitialize the client
        const resetResult = await resetSupabaseConnection();
        return !!resetResult.success;
      }

      return true;
    } catch (error) {
      // Try to reconnect on any error
      try {
        await resetSupabaseConnection();
        return true;
      } catch (resetError) {
        return false;
      }
    } finally {
      isPingingSupabaseRef.current = false;
    }
  };

  // Effect to setup Capacitor App resume listener for Supabase pre-warming
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleAppResume = () => {
      // Run pre-warming in background without blocking UI
      setTimeout(() => {
        prewarmSupabaseConnection().catch(() => {});
      }, 100); // Small delay to ensure app is fully resumed
    };

    // Add the resume listener
    const resumeListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        handleAppResume();
      }
    });

    // Also run initial pre-warming check on mount
    setTimeout(() => {
      prewarmSupabaseConnection().catch(() => {});
    }, 1000);

    return () => {
      resumeListener.then(listener => listener.remove());
    };
  }, []);

  // Effect to watch for app state changes
  useEffect(() => {
    // Skip network listeners for non-native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const setupNetworkListeners = async () => {
      try {
        // Initial network status
        const status = await Network.getStatus();
        setNetworkConnected(status.connected);

        // Listen for network status changes
        const networkListener = await Network.addListener('networkStatusChange', (status) => {
          setNetworkConnected(status.connected);

          // If network reconnects, verify Supabase connection
          if (status.connected) {
            // Run in the next tick to allow iOS networking to stabilize
            setTimeout(async () => {
              await pingSupabaseAndReconnect();

              // If user is null but we were previously authenticated, try to refresh
              if (!user && localStorage.getItem('santiyex-session-expiry')) {
                refreshUser();
              }
            }, 1000);
          }
        });

        // Return cleanup function
        return () => {
          networkListener.remove();
        };
      } catch (error) {
        return () => {}; // Empty cleanup function in case of error
      }
    };

    // Setup network listeners
    const cleanupListeners = setupNetworkListeners();

    // Return cleanup function
    return () => {
      cleanupListeners.then(cleanup => cleanup()).catch(() => {});
    };
  }, [user]);

  // Schedule session expiry check every 10 minutes
  useEffect(() => {
    if (!user) return; // only when logged-in

    const checkExpiryInterval = setInterval(async () => {
      try {
        const valid = await isSessionValid();
        if (!valid && user) {
          signOut();
          toast({
            title: "Oturum Süresi Doldu",
            description: "Güvenliğiniz için 24 saat sonra otomatik olarak çıkış yapıldı. Lütfen tekrar giriş yapın.",
            variant: "default"
          });
        }
      } catch (error) {
        // ignore expiry-check errors
      }
    }, 600000); // Check every 10 minutes

    return () => clearInterval(checkExpiryInterval);
  }, [!!user]);

  // FaceID auto-login (native only). Invoked when the initial auth event reports
  // no session. Runs OUTSIDE the Supabase auth lock (scheduled via setTimeout),
  // so awaiting auth calls here is safe. A successful sign-in emits SIGNED_IN,
  // which the synchronous auth listener turns into app state.
  const attemptFaceIDLogin = useCallback(async () => {
    if (faceIDAttemptedRef.current) {
      return;
    }
    faceIDAttemptedRef.current = true;

    if (!Capacitor.isNativePlatform()) {
      setLoading(false);
      return;
    }

    try {
      const savedEmail = localStorage.getItem('faceIDEmail');
      if (!savedEmail) {
        setLoading(false);
        return;
      }

      const FaceIDService = (await import('@/services/FaceIDService')).default;
      const isFaceIDAvailable = await FaceIDService.isAvailable();

      if (!isFaceIDAvailable) {
        FaceIDService.clearStoredCredentials();
        setLoading(false);
        toast({
          title: "Face ID Kullanılamıyor",
          description: "Bu cihazda Face ID desteklenmiyor veya etkinleştirilmemiş.",
          variant: "destructive",
        });
        return;
      }

      const passwordKey = `faceID_pwd_${savedEmail}`;
      const encryptedPassword = localStorage.getItem(passwordKey);
      if (!encryptedPassword) {
        localStorage.removeItem('faceIDEmail'); // Clear invalid configuration
        setLoading(false);
        return;
      }

      if (Capacitor.getPlatform() === 'ios') {
        toast({
          title: "Face ID Girişi",
          description: "Face ID ile otomatik giriş yapılacak, lütfen bekleyin...",
          variant: "default",
        });
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      const result = await FaceIDService.authenticate("Santiyex'e giriş yapmak için Face ID kullanın");

      if (result.success) {
        const password = atob(encryptedPassword);
        const { data, error } = await authAPI.signIn(savedEmail, password);

        if (!error && data && data.user) {
          // SIGNED_IN will set the user; just persist expiry + warm OneSignal.
          setSessionExpiry();

          setTimeout(async () => {
            try {
              const { initializeOneSignalAfterLogin } = await import('@/integrations/onesignal');
              await initializeOneSignalAfterLogin(data.user.id);
            } catch (error) {
              // OneSignal failure should not affect login
            }
          }, 100);

          toast({
            title: "Face ID Giriş Başarılı",
            description: "Hoş geldiniz!",
          });
          return;
        }

        // Stored credentials no longer valid
        FaceIDService.clearStoredCredentials();
        setLoading(false);
        toast({
          title: "Face ID Giriş Başarısız",
          description: "Giriş bilgileriniz güncel değil. Lütfen manuel olarak giriş yapın.",
          variant: "destructive",
        });
        return;
      }

      // Authentication did not succeed
      setLoading(false);
      if (result.error && !result.error.includes("cancel")) {
        FaceIDService.clearStoredCredentials();
      }

      if (result.error && result.error.includes("cancel")) {
        toast({
          title: "Face ID İptal Edildi",
          description: "Face ID doğrulaması iptal edildi. Lütfen manuel olarak giriş yapın.",
          variant: "default",
        });
      } else if (result.error && result.error.includes("permission")) {
        toast({
          title: "Face ID İzni Gerekli",
          description: "Face ID kullanımı için uygulama izinlerini kontrol edin.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Face ID Doğrulama Başarısız",
          description: "Face ID ile doğrulama yapılamadı. Lütfen manuel olarak giriş yapın.",
          variant: "destructive",
        });
      }
    } catch (faceIDError) {
      authDebug('attemptFaceIDLogin:error', faceIDError);
      setLoading(false);
      toast({
        title: "Face ID Hatası",
        description: "Face ID kullanılırken bir hata oluştu.",
        variant: "destructive",
      });
    }
  }, []);

  // Keep the ref pointing at the latest FaceID routine.
  attemptFaceIDLoginRef.current = attemptFaceIDLogin;

  // SINGLE SOURCE OF TRUTH for auth state.
  //
  // The callback is intentionally SYNCHRONOUS: it must never `await` a Supabase
  // auth call (getSession/refreshSession/etc.), because it runs while the client
  // holds its internal auth lock and awaiting inside it deadlocks the client
  // (the bug that left the app stuck on the loading screen). We derive the user
  // directly from the `session` argument we already receive, and defer all
  // network/DB work to the hydration effect below.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      authDebug('onAuthStateChange', {
        event,
        hasSession: !!session,
        sessionUserId: session?.user?.id ?? null,
      });

      if (event === 'SIGNED_OUT' || !session?.user) {
        setUser(null);
        setProfile(null);
        setProfileLoading(false);
        setLoading(false);

        // No persisted session at startup: try FaceID auto-login on native.
        if (event === 'INITIAL_SESSION') {
          if (Capacitor.isNativePlatform() && !faceIDAttemptedRef.current) {
            setLoading(true);
            setTimeout(() => {
              attemptFaceIDLoginRef.current?.();
            }, 0);
          }
        }
        return;
      }

      // We have a valid session — set a basic user immediately (no network).
      const basicUser = {
        id: session.user.id,
        email: session.user.email,
        name: session.user.email?.split('@')[0] || 'User',
      } as User;

      setUser(prev => (prev && prev.id === basicUser.id ? prev : basicUser));
      setLoading(false);

      // Persist session expiry directly from the session (no getSession call).
      try {
        const expiresAt = session.expires_at
          ? session.expires_at
          : Math.floor(Date.now() / 1000) + 86400;
        localStorage.setItem('santiyex-session-expiry', expiresAt.toString());
      } catch (e) {
        // ignore localStorage errors
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Background hydration — runs OUTSIDE the auth lock whenever the signed-in
  // user id changes. Fetches the full user record and profile. Failures here
  // must never clear `user` (the basic session user is enough to use the app).
  useEffect(() => {
    if (!user?.id) {
      return;
    }

    const userId = user.id;
    let cancelled = false;

    (async () => {
      // 1) Use cached full user immediately if it matches.
      try {
        const cachedUserString = localStorage.getItem('santiyex-user-cache');
        if (cachedUserString) {
          const cachedUser = JSON.parse(cachedUserString) as User;
          if (cachedUser.id === userId && !cancelled) {
            setUser(prev => (prev && prev.id === userId ? cachedUser : prev));
          }
        }
      } catch (e) {
        // ignore cache errors
      }

      // 2) Refresh full user from the API in the background.
      try {
        const fullUser = await authAPI.getCurrentUser();
        if (fullUser && fullUser.id === userId && !cancelled) {
          setUser(prev => (prev && prev.id === userId ? fullUser : prev));
          try {
            localStorage.setItem('santiyex-user-cache', JSON.stringify(fullUser));
          } catch (e) {
            // ignore localStorage errors
          }
        }
      } catch (e) {
        authDebug('hydrate:getCurrentUser:error', e);
      }

      // 3) Fetch the profile.
      if (!cancelled) {
        await fetchProfile(userId);
      }

      // 4) Enforce minimum app version before allowing app usage.
      if (!cancelled) {
        const canEnter = await checkVersionGate();
        if (!canEnter) {
          toast({
            title: 'Update Required',
            description: 'Please update the app to continue.',
            variant: 'destructive',
          });
          await signOut();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, checkVersionGate]);

  // Effect to handle face-id-login-redirect flag
  useEffect(() => {
    const handleFaceIDRedirect = async () => {
      const hasFaceIDRedirect = localStorage.getItem('face-id-login-redirect') === 'true';

      if (hasFaceIDRedirect && !user) {
        try {
          // Try to load user from cache
          const cachedUserString = localStorage.getItem('santiyex-user-cache');
          if (cachedUserString) {
            const cachedUser = JSON.parse(cachedUserString) as User;
            setUser(cachedUser);

            // Clear the flag after handling it
            localStorage.removeItem('face-id-login-redirect');
          } else {
            // If no cached user but we have a flag, try refreshing
            refreshUser();
          }
        } catch (error) {
          localStorage.removeItem('face-id-login-redirect');
        }
      }
    };

    handleFaceIDRedirect();
  }, [location.pathname]);

  const signOut = async () => {
    // Create a timeout promise to ensure logout never hangs forever
    const logoutTimeout = new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 15000); // 15 second timeout
    });

    const performLogout = async () => {
      // Track logout and session end before clearing user data
      if (user && sessionStartTime.current) {
        try {
          const sessionEnd = new Date();
          const sessionDuration = sessionEnd.getTime() - sessionStartTime.current.getTime();

          // Log logout activity
          await logActivity(user.id, ActivityActions.LOGOUT, {
            logoutTime: sessionEnd.toISOString(),
            sessionDuration: sessionDuration,
            pagesVisited: Array.from(visitedPages.current),
            actionsPerformed: actionsCount.current
          });

          // Log comprehensive session activity
          await logSessionActivity(user.id, {
            sessionStart: sessionStartTime.current,
            sessionEnd: sessionEnd,
            pagesVisited: Array.from(visitedPages.current),
            actionsPerformed: actionsCount.current,
            totalTimeSpent: sessionDuration
          });
        } catch (error) {
          // ignore analytics errors
        }
      }

      // Set loading state at the beginning
      setLoading(true);

      try {
        // Log out from RevenueCat if on a mobile platform
        if (Capacitor.isNativePlatform()) {
          try {
            // Use PurchaseService logout method instead of Purchases.logOut()
            // to avoid creating anonymous IDs which can cause transaction loops
            const PurchaseService = (await import('@/services/PurchaseService')).default;

            // Add timeout to RevenueCat logout
            const rcLogoutPromise = PurchaseService.logoutFromRevenueCat();
            const rcTimeout = new Promise<boolean>((resolve) => {
              setTimeout(() => {
                resolve(false);
              }, 5000);
            });

            await Promise.race([rcLogoutPromise, rcTimeout]);
          } catch (rcError) {
            // Continue with sign out process even if RevenueCat logout fails
          }
        }

        // Log out from OneSignal (remove External ID) if on a mobile platform
        if (Capacitor.isNativePlatform()) {
          try {
            const { enhancedOneSignalLogout } = await import('@/integrations/onesignal/enhanced-auth-flow');

            await enhancedOneSignalLogout({
              silentMode: true, // Don't show additional toasts during logout
              timeout: 5000 // 5 second timeout
            });
          } catch (osError) {
            // Continue with sign out process even if OneSignal logout fails
          }
        }

        // Sign out from Supabase
        await authAPI.signOut();
      } catch (error) {
        // ignore sign-out errors; cleanup below always runs
      }
    };

    try {
      // Race the logout process against the timeout
      await Promise.race([performLogout(), logoutTimeout]);
    } catch (logoutError) {
      // ignore; cleanup below always runs
    }

    // ALWAYS execute cleanup regardless of logout success/failure
    sessionStartTime.current = null;
    visitedPages.current.clear();
    actionsCount.current = 0;
    faceIDAttemptedRef.current = false;

    setUser(null);
    setProfile(null);
    setProfileLoading(false);

    // Clear localStorage
    localStorage.removeItem('santiyex-session-expiry');
    localStorage.removeItem('santiyex-user-cache');
    localStorage.removeItem('santiyex-user-lock-status');
    localStorage.removeItem('faceIDEmail');
    localStorage.removeItem('face-id-login-redirect');

    // Discard any saved form drafts so they can't surface for the next account.
    void clearAllDrafts();

    // Reset loading state before navigation
    setLoading(false);

    // Navigate to login page
    navigate('/login', { replace: true });

    // Add a safety timeout to ensure loading is reset (in case of race conditions)
    setTimeout(() => {
      setLoading(false);
      setProfileLoading(false);
    }, 1000);
  };

  // --- Add updateUser Function ---
  const updateUser = async (updates: Partial<UserProfile>) => {
      if (!user || !profile) {
           toast({ variant: "destructive", title: "Güncelleme Hatası", description: "Kullanıcı veya profil bulunamadı." });
           throw new Error("User or profile not available for update.");
      }

      try {
          const { data, error } = await supabase
              .from('users')
              .update(updates)
              .eq('id', user.id)
              .select() // Select the updated row
              .single(); // Expecting a single row back

          if (error) {
              toast({ variant: "destructive", title: "Profil Güncellenemedi", description: error.message || "Bir hata oluştu." });
              throw error; // Re-throw error for ProfileDetailScreen to catch if needed
          }

          if (data) {
              setProfile(data as UserProfile); // Update local profile state
              toast({ variant: "default", title: "Profil Güncellendi", description: "Bilgileriniz başarıyla kaydedildi." });
          } else {
               // Re-fetch profile to be sure
               await fetchProfile(user.id);
          }
      } catch (err) {
          toast({ variant: "destructive", title: "Profil Güncellenemedi", description: "Beklenmeyen bir hata oluştu." });
          throw err; // Re-throw
      }
  };
  // --- End updateUser Function ---

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signOut,
        refreshUser,
        isAuthenticated: !!user,
        isResuming,
        networkReady: resumeReady,
        profile,
        profileLoading,
        updateUser,
        supabaseReady,
        updateRequired,
        updateLink,
        requiredAppVersion,
        deviceAppVersion,
        checkVersionGate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
